"use client";

import { useMemo } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";

import { usePropertySearchStore } from "@/stores/usePropertySearchStore";
import { getPropertiesList, searchProperties } from "@/actions/realstate/realstate.action";
import { SearchFilters } from "@/app/types"

const DEFAULT_PAGE_SIZE = 24;

export const usePropertySearch = () => {
  const { filters, validateFilters } = usePropertySearchStore();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentPage = Number(searchParams.get("page") || "1");

  // Determine if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.location ||
      filters.category ||
      filters.keyword ||
      filters.minPrice ||
      filters.maxPrice ||
      filters.bedrooms ||
      filters.bathrooms ||
      (filters.amenities?.length > 0)
    );
  }, [filters]);

  // Stable filter key
  const queryKeyFilters = useMemo(() => ({
    address: filters.location?.address ?? null,
    lat: filters.location?.coordinates?.lat ?? null,
    lng: filters.location?.coordinates?.lon ?? null,
    category: filters.category?.id ?? null,
    keyword: filters.keyword?.id ?? null,
    minPrice: filters.minPrice ?? null,
    maxPrice: filters.maxPrice ?? null,
    bedrooms: filters.bedrooms ?? null,
    bathrooms: filters.bathrooms ?? null,
    amenities: filters.amenities ?? [],
    page: currentPage,
  }), [filters, currentPage]);

  // Default properties
  const {
    data: defaultProperties = { items: [], totalPages: 0 },
    isLoading: isDefaultLoading,
  } = useQuery({
    queryKey: ["default-properties", currentPage],
    queryFn: async () => {
      const res = await getPropertiesList(currentPage, DEFAULT_PAGE_SIZE);
      return {
        items: res?.data?.data?.items ?? [],
        totalPages: res?.data?.data?.totalPages ?? 0,
      };
    },
    enabled: !hasActiveFilters,
    staleTime: 1000 * 60 * 5,
  });

  // Filtered properties
  const {
    data: filteredProperties = { items: [], totalPages: 0 },
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    refetch: refetchProperties,
  } = useQuery({
    queryKey: ["search-properties", queryKeyFilters],
    queryFn: async () => {
      const validation = validateFilters("compact");
      if (!validation.success) {
        console.error("Invalid filters:", validation.errors);
        return { items: [], totalPages: 0 };
      }
      const transformedFilters: SearchFilters = {
        ...filters,
        location: filters.location ? {
          coordinates: filters.location.coordinates,
          country: filters.location.country || '',
          state: filters.location.state || '',
          city: filters.location.city || ''
        } : null
      };
      const res = await searchProperties(transformedFilters, currentPage, DEFAULT_PAGE_SIZE);
      return {
        items: res?.data?.data?.items ?? [],
        totalPages: res?.data?.data?.totalPages ?? 0,
      };
    },
    enabled: hasActiveFilters && validateFilters("compact").success,
  });

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", newPage.toString());
    router.push(`${pathname}?${params.toString()}`);
  };

  const executeSearch = () => {
    const validation = validateFilters("compact");
    if (!validation.success) {
      console.error("Invalid filters:", validation.errors);
      return false;
    }

    const params = new URLSearchParams();
    if (filters.location?.address) params.set("address", filters.location.address);
    if (filters.category?.id) params.set("category", filters.category.id);
    if (filters.keyword?.id) params.set("keyword", filters.keyword.id);
    if (filters.minPrice !== undefined) params.set("minPrice", String(filters.minPrice));
    if (filters.maxPrice !== undefined) params.set("maxPrice", String(filters.maxPrice));
    if (filters.bedrooms !== undefined) params.set("bedrooms", String(filters.bedrooms));
    if (filters.bathrooms !== undefined) params.set("bathrooms", String(filters.bathrooms));
    if (filters.amenities?.length) {
      for (const amenity of filters.amenities) {
        params.append("amenities", amenity);
      }
    }

    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);

    // Trigger re-fetch after route update
    setTimeout(() => refetchProperties(), 0);

    return true;
  };

  return {
    properties: hasActiveFilters ? filteredProperties.items : defaultProperties.items,
    totalPages: hasActiveFilters ? filteredProperties.totalPages : defaultProperties.totalPages,
    isLoading: isDefaultLoading || isSearchLoading,
    isFetching: isSearchFetching,
    currentPage,
    handlePageChange,
    executeSearch,
    hasActiveFilters,
  };
};
